import { Suspense, When } from '@teamsparta/react';
import { Flex } from '@teamsparta/stack-flex';
import { ArrowChevronDown, ArrowRight } from '@teamsparta/stack-icons';
import { Text } from '@teamsparta/stack-text';
import { vars } from '@teamsparta/stack-tokens';
import { AnimatePresence, motion } from 'framer-motion';
import { useRef } from 'react';

import { LoggingClick } from '@/components/common/LoggingClick';
import Portal from '@/components/common/Portal';
import type { GNBDropdownMenu } from '@/components/gnb/constants';
import { useClickAway, useIsDesktop } from '@/hooks';

import { Badge } from '../../../Badge';
import { GNB_DROP_DOWN_MENU_CONTENT_ID } from './constants';
import * as S from './style';

interface Props extends GNBDropdownMenu {
  opened?: boolean;
  open?: () => void;
  close: () => void;
}

export function DropdownMenu({
  name: dropdownMenuName,
  items,
  opened = false,
  open,
  close,
}: Props) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useClickAway<HTMLDivElement>((event) => {
    if (!opened) {
      return;
    }

    if (triggerRef.current?.contains(event.target as Node)) {
      return;
    }

    close();
  });
  const isDesktop = useIsDesktop();

  return (
    <>
      <LoggingClick
        logName='hh_gnb_button_click'
        data={{ button_text: dropdownMenuName }}
      >
        <S.DropdownMenuTrigger
          ref={triggerRef}
          opened={opened}
          onClick={() => {
            if (opened) {
              close?.();
            } else {
              open?.();
            }
          }}
        >
          <Text as='span' font='bodyM' color={vars.text.secondary}>
            {dropdownMenuName}
          </Text>
          <motion.span
            animate={{ transform: opened ? 'rotate(180deg)' : 'rotate(0deg)' }}
            transition={{ duration: 0.2 }}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <ArrowChevronDown size={16} color={vars.icon.secondary} />
          </motion.span>
        </S.DropdownMenuTrigger>
      </LoggingClick>
      <When condition={isDesktop}>
        <Portal selector={`#${GNB_DROP_DOWN_MENU_CONTENT_ID}`}>
          <AnimatePresence>
            {opened && (
              <S.DropdownMenuContent
                ref={contentRef}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <Flex.Column
                  style={{
                    width: '984px',
                  }}
                >
                  <Flex
                    style={{
                      width: '984px',
                      justifyContent: 'center',
                    }}
                  >
                    {items.map(
                      (
                        {
                          name,
                          description,
                          detailItems,
                          iconImage,
                          backgroundImage,
                        },
                        index,
                      ) => (
                        <Flex key={name}>
                          <S.Container>
                            {backgroundImage && (
                              <S.AbsoluteImageWrapper
                                src={`${process.env.NEXT_PUBLIC_CDN_BASE_URL}/hanghae99${backgroundImage}`}
                                width={472}
                                height={269}
                                alt='배경이미지'
                              />
                            )}
                            <Flex.Column>
                              <Flex
                                gap={16}
                                align='center'
                                style={{ marginBottom: '20px' }}
                              >
                                {iconImage && (
                                  <S.IconImageWrapper
                                    src={`${process.env.NEXT_PUBLIC_CDN_BASE_URL}/hanghae99${iconImage}`}
                                    width={70}
                                    height={70}
                                    alt='아이콘'
                                    unoptimized
                                  />
                                )}
                                <Flex.Column
                                  align='start'
                                  style={{ flex: 1 }}
                                  gap={8}
                                >
                                  <Text
                                    as='h4'
                                    font='subTitle2'
                                    color={vars.text.primary}
                                  >
                                    {name}
                                  </Text>
                                  <Text
                                    as='p'
                                    font='bodyLong'
                                    color={vars.text.tertiary}
                                  >
                                    {description}
                                  </Text>
                                </Flex.Column>
                              </Flex>

                              <Flex.Column gap={8}>
                                {detailItems?.map(
                                  ({
                                    name: detailName,
                                    url,
                                    courseType,
                                    newTab,
                                    customBadge,
                                  }) => (
                                    <LoggingClick
                                      key={detailName}
                                      logName='hh_gnb_button_click'
                                      data={{ button_text: detailName }}
                                    >
                                      <S.StyledLink
                                        href={url}
                                        target={newTab ? '_blank' : '_self'}
                                        rel='noopener noreferrer'
                                      >
                                        <Flex align='center' gap={6} fullWidth>
                                          <Text
                                            as='span'
                                            font='bodyB'
                                            color={vars.text.secondary}
                                          >
                                            {detailName}
                                          </Text>
                                          <Suspense clientOnly>
                                            {dropdownMenuName === '교육' && (
                                              <Badge
                                                courseType={courseType}
                                                customBadge={customBadge}
                                              />
                                            )}
                                          </Suspense>
                                        </Flex>
                                        <ArrowRight
                                          size={20}
                                          color={vars.icon.tertiary}
                                        />
                                      </S.StyledLink>
                                    </LoggingClick>
                                  ),
                                )}
                              </Flex.Column>
                            </Flex.Column>
                          </S.Container>
                          {index < items.length - 1 && <S.Divider />}
                        </Flex>
                      ),
                    )}
                  </Flex>
                </Flex.Column>
              </S.DropdownMenuContent>
            )}
          </AnimatePresence>
        </Portal>
      </When>
    </>
  );
}
